import { SpotlightItem, SpotlightItemType } from '@thoughtspot/global-nav';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { useCallback, useMemo } from 'react';
import {
    GetMetadataListQuery,
    ListType,
    SubTypes,
    useGetMetadataListLazyQuery as _useGetMetadataListLazyQuery,
} from '../../services/generated/graphql-types';

type MetadataObject = NonNullable<
    NonNullable<GetMetadataListQuery['getMetadataList']>['objects']
>[number];

/**
 * Lazily fetches the list of models (worksheets) visible to the user,
 * matching what the Data page shows. Triggered by calling activate().
 *
 * Used when the user selects the "Models" filter in the command palette.
 */
export function useSpotlightModelItems() {
    const [fetchModels, { data, loading }] = _useGetMetadataListLazyQuery();

    const activate = useCallback(() => {
        fetchModels({
            fetchPolicy: 'network-only',
            variables: {
                params: {
                    type: ListType.LogicalTable,
                    subtypes: [SubTypes.Worksheet],
                    batchSize: 100,
                    offset: 0,
                    sortAscending: false,
                    sort: 'MODIFIED',
                    showHidden: false,
                },
            },
        });
    }, [fetchModels]);

    const modelItems = useMemo<SpotlightItem[]>(() => {
        const objects = data?.getMetadataList?.objects;
        if (!objects) return [];
        return objects
            .filter(
                (obj: MetadataObject): obj is NonNullable<MetadataObject> =>
                    !!obj?.id && !!obj?.name,
            )
            .map((obj: NonNullable<MetadataObject>) => ({
                id: `model-spotlight-${obj.id}`,
                label: obj.name,
                description:
                    obj.authorDisplayName || obj.authorName
                        ? `by ${obj.authorDisplayName ?? obj.authorName}`
                        : undefined,
                icon: IconID.SAVE_WORKSHEET,
                type: SpotlightItemType.NAVIGATION,
                path: `/data/tables/${obj.id}`,
                keywords: ['model', 'worksheet', 'data'],
            }));
    }, [data]);

    return { modelItems, modelItemsLoading: loading, activate };
}
